import { create } from "zustand";
import { searchProductsAction } from "@/server/actions/products/search";
import { pusherClient } from "@/lib/pusher";
import type { SimpleProduct } from "@/types";

// Define the search result type that extends SimpleProduct
export type SearchResult = SimpleProduct & {
  brand_slug: string;
  url: string;
  brand_id: string;
  part_number: string;
  title: string;
  image: string[] | null;
  status: string;
};

interface SearchState {
  query: string;
  results: SearchResult[];
  isLoading: boolean;
  error: string | null;
  recentSearches: string[];
  currentBrandFilter: string | null;
  companyId?: string;

  // Actions
  setQuery: (query: string) => void;
  setResults: (results: SearchResult[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  addRecentSearch: (query: string) => void;
  setBrandFilter: (brandId: string | null) => void;
  setCompanyId: (companyId?: string) => void;

  // Search function
  search: (query: string) => Promise<void>;

  // Clear functions
  clearSearch: () => void;
  clearResults: () => void;
  clearRecentSearches: () => void;
}

const MAX_RECENT_SEARCHES = 5;

export const useSearchStore = create<SearchState>((set, get) => ({
  query: "",
  results: [],
  isLoading: false,
  error: null,
  recentSearches: [],
  currentBrandFilter: null,
  companyId: undefined,

  setQuery: (query) => set({ query }),
  setResults: (results) => set({ results }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setBrandFilter: (brandId) => set({ currentBrandFilter: brandId }),
  setCompanyId: (companyId) => set({ companyId }),

  addRecentSearch: (query) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return;

    const currentSearches = get().recentSearches;
    // Remove if exists already (to reorder)
    const filteredSearches = currentSearches.filter(
      (s) => s !== normalizedQuery
    );
    // Add to beginning and limit length
    const newSearches = [normalizedQuery, ...filteredSearches].slice(
      0,
      MAX_RECENT_SEARCHES
    );
    set({ recentSearches: newSearches });

    // Persist to localStorage if in browser environment
    if (typeof window !== "undefined") {
      localStorage.setItem("recentSearches", JSON.stringify(newSearches));
    }
  },

  search: async (query) => {
    const state = get();
    const normalizedQuery = query.trim();

    if (!normalizedQuery) {
      set({ results: [], query: "" });
      return;
    }

    set({ isLoading: true, query: normalizedQuery, error: null });

    try {
      // Server action will handle passing companyId based on session
      const response = await searchProductsAction(normalizedQuery);

      if (response && response.success && response.results) {
        let results = response.results as SearchResult[];

        // Filter by brand if a brand filter is active
        if (state.currentBrandFilter) {
          results = results.filter(
            (product) => product.brand_id === state.currentBrandFilter
          );
        }

        set({ results, isLoading: false });

        // Add to recent searches if we got results
        if (results.length > 0) {
          state.addRecentSearch(normalizedQuery);
        }
      } else {
        set({
          results: [],
          isLoading: false,
          error: (response && response.error) || "No results found",
        });
      }
    } catch (error) {
      console.error("Error searching products:", error);
      set({
        results: [],
        isLoading: false,
        error: "An error occurred while searching. Please try again.",
      });
    }
  },

  clearSearch: () => set({ query: "", results: [], error: null }),
  clearResults: () => set({ results: [], error: null }),
  clearRecentSearches: () => {
    set({ recentSearches: [] });
    if (typeof window !== "undefined") {
      localStorage.removeItem("recentSearches");
    }
  },
}));

// Initialize Pusher listeners for real-time updates
export function initSearchPusher() {
  if (typeof window === "undefined") return;

  const channel = pusherClient.subscribe("products");

  // If product is created, updated, or deleted, we should invalidate our search results
  channel.bind("product-created", () => {
    // Trigger a re-search with current query if we have results showing
    const { query, results } = useSearchStore.getState();
    if (query && results.length > 0) {
      useSearchStore.getState().search(query);
    }
  });

  channel.bind("product-updated", () => {
    const { query, results } = useSearchStore.getState();
    if (query && results.length > 0) {
      useSearchStore.getState().search(query);
    }
  });

  channel.bind("products-deleted", () => {
    const { query, results } = useSearchStore.getState();
    if (query && results.length > 0) {
      useSearchStore.getState().search(query);
    }
  });

  return () => {
    pusherClient.unsubscribe("products");
  };
}
